package er.prateek.crossly.ui.components

import androidx.compose.animation.core.animateDpAsState
import androidx.compose.animation.core.spring
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.hoverable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.interaction.collectIsHoveredAsState
import androidx.compose.foundation.interaction.collectIsPressedAsState
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import er.prateek.crossly.ui.theme.Depth
import er.prateek.crossly.ui.theme.Radius
import er.prateek.crossly.ui.theme.Spacing
import er.prateek.crossly.ui.theme.game

/**
 * Panel surface with the same extruded slab as [GameButton], so cards and
 * buttons share one physical language. [accentGlow] tints the border and slab
 * for cards that need to advertise themselves (the daily-puzzle card, the
 * active tier).
 */
@Composable
fun GameCard(
    modifier: Modifier = Modifier,
    onClick: (() -> Unit)? = null,
    accentGlow: Color? = null,
    contentPadding: PaddingValues = PaddingValues(Spacing.lg),
    cornerRadius: Dp = Radius.card,
    elevated: Boolean = true,
    content: @Composable ColumnScope.() -> Unit,
) {
    val colors = MaterialTheme.game
    val shape = RoundedCornerShape(cornerRadius)
    val interactionSource = remember { MutableInteractionSource() }
    val pressed by interactionSource.collectIsPressedAsState()
    val hovered by interactionSource.collectIsHoveredAsState()

    val depth = if (elevated) Depth.card else 0.dp
    val travel by animateDpAsState(
        targetValue = when {
            onClick == null -> 0.dp
            pressed -> depth
            hovered -> (-2).dp
            else -> 0.dp
        },
        animationSpec = spring(dampingRatio = 0.6f, stiffness = 1200f),
        label = "cardTravel",
    )

    val borderColor = accentGlow ?: colors.cardBorder
    val slabColor = accentGlow?.copy(alpha = 0.45f) ?: colors.shadow

    // The caller's modifier goes on the *face*, not the wrapper: the slab uses
    // matchParentSize, so a wrapper sized independently of its content would
    // leave the slab jutting out past a face that only wrapped its children.
    Box(modifier = Modifier.padding(bottom = depth)) {
        if (depth > 0.dp) {
            Box(
                modifier = Modifier
                    .matchParentSize()
                    .offset(y = depth)
                    .clip(shape)
                    .background(slabColor),
            )
        }
        Column(
            modifier = modifier
                .offset(y = travel)
                .clip(shape)
                .background(Brush.verticalGradient(listOf(colors.card, colors.cardElevated)))
                .border(if (accentGlow != null) 2.dp else 1.5.dp, borderColor, shape)
                .then(
                    if (onClick != null) {
                        Modifier
                            .hoverable(interactionSource)
                            .clickable(
                                interactionSource = interactionSource,
                                indication = null,
                                onClick = onClick,
                            )
                    } else {
                        Modifier
                    },
                )
                .padding(contentPadding),
            content = content,
        )
    }
}
